import { useMemo } from "preact/hooks";
import type { JSX } from "preact";
import quiz from "./assets/content/quiz.json";
import type { CareerKey } from "./types/quiz";

/* ---------- Types ---------- */

export type RankedCareer = {
  key: CareerKey;
  name: string;
  description: string;
  image: string;
  otherImage : string;
  pct: number;
};

interface ResultsPageProps {
  authoredLinks: any;
  rankedCareers: RankedCareer[];
  pctScores?: Record<CareerKey, number>;
  marketingNode?: JSX.Element | null;
  onRestart: () => void;
}

type CareerMeta = {
  name: string;
  whyThisFits: string;
  yourStartingPoint: string;
  skills?: string[];
  roles?: string[];
  link?: string;
};

type LearnMoreItem = {
  title: string;
  blurb: string;
  img?: string;
  link?: string;
};

/* ---------- Constants ---------- */

const WRAP = "cq-max-w-[1228px] cq-mx-auto cq:px-6 md:cq-px-10";

//const CARD_IMAGES = ["images/glass-roof.png", "images/vacation.png"] as const;

/** Fallback images for the learn-more cards when JSON doesn't specify one */
const LEARN_IMAGES = [
  "images/people-viewing-laptop.png",
  "images/person-pointing-at-graph.png",
  "images/person-asking-question.png",
] as const;

/** Cache JSON-derived maps/arrays at module scope where safe */
const CAREERS_META = quiz.careers as Record<CareerKey, CareerMeta>;
const LEARN_MORE = (quiz as any).learnMore as LearnMoreItem[] | undefined;

/* ---------- UI ---------- */

function SkillsList({ skills }: { skills: string[] }) {
  return (
    <ul className="cq-space-y-2 cq-list-disc cq-pl-0">
      {skills.map((s) => (
        <li
          key={s}
          className="cq-text-base cq-leading-tight cq-gap-2 cq-mb-0 cq-pl-0"
        >
          <span>{s}</span>
        </li>
      ))}
    </ul>
  );
}

function PathCard({
  career,
  imgSrc,
  href,
}: {
  career: RankedCareer;
  imgSrc: string;
  href: string;
}) {
  const meta = CAREERS_META[career.key];
  const name = meta?.name ?? career.name;
  const skills = (meta?.skills ?? []).slice(0, 4);
  const startingPoint = (meta?.roles ?? [])[0] ?? "Analyst / Entry-level role";

  return (
    <div className="cq-bg-white">
      {/* Image */}
      <div className="cq-relative cq-w-full cq-aspect-[16/9]">
        <img
          src={`${import.meta.env.BASE_URL}${imgSrc}`}
          alt={`${name} visual`}
          loading="lazy"
          decoding="async"
          className="cq-absolute cq-inset-0 cq-h-full cq-w-full cq-object-cover"
        />
      </div>

      {/* Full-width yellow title bar */}
      <div className="cq-bg-cfa-highlight cq-text-black cq-px-6 cq-font-bold cq-text-lg md:cq-text-[22px] cq-py-5">
        {name}
      </div>

      {/* Body */}
      <div className="cq-p-6 cq-py-5">
        <div className="cq-grid cq-grid-cols-1 sm:cq-grid-cols-2 cq-gap-6">
          <div>
            <img
              src={`${import.meta.env.BASE_URL}icons/shape.svg`}
              alt="About You - Top Skills"
              loading="lazy"
              decoding="async"
              className="cq-w-[44px] cq-mb-2 cq-h-[43px] cq-aspect-[44/43] cq-object-cover"
            />
            <div className="cq-text-base cq-font-bold">Top Skills</div>
            <ul className="cq-mt-2 cq-space-y-1 cq-list-disc cq-ml-6 cq-pl-0">
              {skills.map((s) => (
                <li
                  key={s}
                  className="cq-text-base cq-leading-tight cq-gap-2 cq-mb-0 cq-pl-0"
                >
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <img
              src={`${import.meta.env.BASE_URL}icons/connect.svg`}
              alt="Starting point"
              loading="lazy"
              decoding="async"
              className="cq-w-[44px] cq-mb-2 cq-h-[44px] cq-aspect-[44/44] cq-object-cover"
            />
            <div className="cq-text-base cq-font-bold">Starting Point</div>
            <ul className="cq-mt-2 cq-space-y-1 cq-list-disc cq-ml-6 cq-pl-0">
              <li className="cq-text-base cq-leading-tight cq-gap-2 cq-mb-0 cq-pl-0">
                <span>{startingPoint}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="cq-flex cq-justify-end">
          <a
            href={href}
            className="!cq-no-underline cq-text-black cq-px-4 cq-py-2 hover:cq-bg-[#f8f8f8] cq-text-lg cq-font-medium cq-underline"
          >
            Learn more
          </a>
        </div>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */

export function ResultsPage({ authoredLinks, rankedCareers, onRestart }: ResultsPageProps) {
  /* compute top & next-two once per rankedCareers change */
  const top = useMemo(() => rankedCareers[0], [rankedCareers]);
  const nextTwo = useMemo(() => rankedCareers.slice(1, 3), [rankedCareers]);
  const nextCTAs = useMemo(() => ([
      authoredLinks && authoredLinks[`career_${nextTwo[0].key}`] || CAREERS_META[nextTwo[0].key]?.link,
      authoredLinks && authoredLinks[`career_${nextTwo[1].key}`] || CAREERS_META[nextTwo[1].key]?.link
    ]), [nextTwo, authoredLinks]);

  const { aboutSkills, firstRole, whyThisFits, yourStartingPoint, cta } =
    useMemo(() => {
      const meta = top ? CAREERS_META[top.key] : undefined;
      return {
        aboutSkills: (meta?.skills ?? []).slice(0, 5),
        firstRole: (meta?.roles ?? [])[0],
        whyThisFits: meta?.whyThisFits,
        yourStartingPoint: meta?.yourStartingPoint,
        cta: authoredLinks && authoredLinks['career_' + top.key] || meta?.link,
      };
    }, [top]);

  /* map learn-more items from JSON (no hardcoded fallback) */
  const learningCards = useMemo(
    () =>
      (LEARN_MORE ?? []).slice(0, 3).map((it, i) => ({
        img: it.img ?? LEARN_IMAGES[i] ?? LEARN_IMAGES[LEARN_IMAGES.length - 1],
        title: it.title,
        blurb: it.blurb,
        link: authoredLinks && authoredLinks[`learn_link_${i + 1}`] || it.link,
      })),
    []
  );

  return (
    <main className="cq-min-h-screen cq-bg-warm-white cq-text-black cq-p-0 cq-m-0">
      {/* HERO */}
      <section className="cq-w-full cq-bg-black cq-text-white cq-px-4 md:cq-px-0">
        <div
          className={`${WRAP} cq-grid cq-grid-cols-1 md:cq-grid-cols-2 cq-items-center cq-gap-0 cq-pt-16 cq-animate-fadeTop`}
        >
          {/* Left */}
          <div className="cq-flex cq-flex-col cq-justify-center cq-items-start cq-gap-6">
            <div>
              <span className="cq-block cq-text-base md:cq-text-lg cq-font-medium cq-uppercase cq-tracking-wide cq-pb-3">
                The career path that fits you best…
              </span>
              <h1 className="cq-mt-4 cq-text-[41px] md:cq-text-6xl cq-leading-[1.05] cq-font-medium highlighted cq-text-black">
                {top?.name ?? "Your top match"}
              </h1>
            </div>
          </div>

          {/* Right */}
          <div className="cq-w-full cq-h-full cq-flex cq-justify-center md:cq-justify-end cq-items-end cq-animate-fadeTop">
            <img
              src={`${import.meta.env.BASE_URL}${top?.image}`}
              alt={top?.name ?? "Top result image"}
              loading="lazy"
              decoding="async"
              className="cq-max-w-full cq-max-h-[320px] lg:cq-max-h-[400px]"
            />
          </div>
        </div>
      </section>

      {/* ABOUT YOU */}
      <section className="cq-w-full cq-animate-fadeTop">
        <div className={`${WRAP} cq-py-10 md:cq-py-14`}>
          <h2 className="cq-font-bold cq-text-3xl md:cq-text-4xl/[50px] cq-px-4 md:cq-px-0 cq-mb-6">
            About you
          </h2>

          <div className="cq-grid cq-grid-cols-1 md:cq-grid-cols-[320px_1fr] cq-gap-8 cq-items-start">
            {/* Left panel */}
            <aside className="cq-flex cq-flex-col cq-bg-white cq-mx-4 md:cq-mx-0">
              <div className="cq-p-5 cq-pt-8 cq-pb-8 cq-w-4/5 cq-mx-auto">
                <img
                  src={`${import.meta.env.BASE_URL}icons/shape.svg`}
                  alt="About You - Top Skills"
                  loading="lazy"
                  decoding="async"
                  className="cq-w-[44px] cq-mb-2 cq-h-[43px] cq-aspect-[44/43] cq-object-cover"
                />
                <h3 className="cq-text-base cq-font-medium cq-my-3">
                  Top skills
                </h3>
                <SkillsList skills={aboutSkills} />
              </div>

              <div className="cq-p-5 cq-pt-8 cq-pb-8 !cq-border-solid cq-border-t-2 cq-border-black cq-w-4/5 cq-mx-auto">
                <img
                  src={`${import.meta.env.BASE_URL}icons/connect.svg`}
                  alt="Starting point"
                  loading="lazy"
                  decoding="async"
                  className="cq-w-[44px] cq-h-[44px] cq-mb-2 cq-aspect-[44/44] cq-object-cover"
                />
                <h3 className="cq-text-base cq-font-medium cq-mb-3">
                  Starting point
                </h3>
                <p className="cq-text-base">
                  {firstRole ?? "Analyst / Entry-level role"}
                </p>
              </div>
            </aside>

            {/* Right panel */}
            <div className="lg:cq-ml-40 cq-grid cq-gap-0 cq-p-4 md:cq-p-0">
              {/* Row 1: Why this fits — title spans full width, text narrowed on the right */}
              <div className="cq-grid cq-grid-cols-1  cq-gap-x-8 cq-items-start !cq-border-solid cq-border-y-2 cq-py-8">
                <span className="cq-col-span-full cq-text-3xl cq-font-medium cq-leading-none cq-select-none cq-block cq-mb-4">
                  Why this fits
                </span>

                <div
                  className="cq-text-base cq-text-black cq-flex cq-flex-col cq-gap-4"
                  dangerouslySetInnerHTML={{ __html: whyThisFits || "" }}
                />
              </div>

              <div className="cq-grid cq-grid-cols-1  cq-gap-x-8 cq-items-start !cq-border-solid cq-border-b-2 cq-py-8">
                <span className="cq-col-span-full cq-text-3xl cq-font-medium cq-leading-none cq-select-none cq-block cq-mb-4">
                  Your starting point
                </span>
                <p className="cq-text-base cq-text-black">
                  {yourStartingPoint}
                </p>
              </div>

              <div className="cq-flex cq-items-center cq-gap-4 cq-pt-8">
                <a
                  href={cta}
                  className="!cq-no-underline cq-inline-flex cq-justify-center cq-px-4 cq-py-2 cq-text-sm md:cq-text-lg cq-bg-cfa-blue-550 cq-font-medium cq-text-white cq-rounded-full hover:cq-bg-[#3676FF] active:cq-brightness-105 cq-h-[40px] md:cq-h-[51px]"
                >
                  Explore this path
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OTHER PATHS */}
      <section className="cq-w-full cq-bg-cfa-bright-blue-30 cq-px-4 md:cq-px-0">
        <div className={`${WRAP} cq-py-12`}>
          <h2 className="cq-text-3xl md:cq-text-[34px] cq-font-bold cq-mb-6">
            Other career paths where you would shine
          </h2>

          <p className="cq-pb-6">
            Based on your results, you might thrive in these areas of finance…
          </p>

          <div className="cq-grid cq-grid-cols-1 md:cq-grid-cols-2 cq-gap-6">
            {nextTwo.map((c, i) => (
              <PathCard
                key={c.key}
                career={c}
                imgSrc={c.otherImage}
                href={nextCTAs[i]}
              />
            ))}
          </div>
        </div>
      </section>

      {/* LEARNING SECTION */}
      {learningCards.length > 0 && (
        <section className="cq-w-full">
          <div className={`${WRAP} cq-px-4 cq-py-12`}>
            <h2 className="cq-text-3xl md:cq-text-[34px] cq-font-bold cq-mb-6 cq-mb-6">
              Want to learn more about finance first?
            </h2>

            <div className="cq-grid cq-grid-cols-1 md:cq-grid-cols-3 cq-gap-6">
              {learningCards.map((card) => {
                const Title = (
                  <h3 className="cq-text-xl cq-font-bold">
                    {card.link ? (
                      <a
                        href={card.link}
                        className="!cq-no-underline hover:!cq-underline cq-text-black"
                        rel="noopener noreferrer"
                      >
                        {card.title}
                      </a>
                    ) : (
                      <span>{card.title}</span>
                    )}
                  </h3>
                );

                return (
                  <div key={card.title} className="cq-bg-white">
                    <div className="cq-relative cq-w-full cq-aspect-[4/3]">
                      <img
                        src={`${import.meta.env.BASE_URL}${card.img}`}
                        alt={card.title}
                        loading="lazy"
                        decoding="async"
                        className="cq-absolute cq-inset-0 cq-h-full cq-w-full cq-object-cover"
                      />
                    </div>
                    <div className="cq-p-4">
                      {Title}
                      <p className="cq-mt-1 cq-text-base cq-mb-0">{card.blurb}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* READY TO BEGIN */}
      <section className="cq-w-full">
        <div className={`${WRAP} cq-pb-8 lg:cq-pt-8`}>
          <h2 className="cq-text-3xl md:cq-text-[38px] cq-font-bold cq-mb-0 cq-text-center">
            Want to update your answers?
          </h2>

          <div className="cq-flex cq-flex-col">
            {/*  CTA */}
            <div className="cq-pt-8 lg:cq-pt-8 cq-flex cq-flex-col cq-justify-center cq-p-4 md:cq-px-0 cq-items-center">
                <button
                  type="button"
                  className="cq-inline-flex cq-items-center cq-gap-2 cq-px-3 cq-py-2 cq-text-sm md:cq-text-lg hover:cq-bg-black/5 cq-whitespace-nowrap cq-rounded-full cq-bg-transparent !cq-border !cq-border-solid !cq-border-black"
                  onClick={onRestart}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="25"
                    height="25"
                    viewBox="0 0 25 25"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_167_11077)">
                      <path
                        d="M12.5 5.5V1.5L7.5 6.5L12.5 11.5V7.5C15.81 7.5 18.5 10.19 18.5 13.5C18.5 16.81 15.81 19.5 12.5 19.5C9.19 19.5 6.5 16.81 6.5 13.5H4.5C4.5 17.92 8.08 21.5 12.5 21.5C16.92 21.5 20.5 17.92 20.5 13.5C20.5 9.08 16.92 5.5 12.5 5.5Z"
                        fill="black"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_167_11077">
                        <rect
                          width="24"
                          height="24"
                          fill="white"
                          transform="translate(0.5 0.5)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                  Restart quiz
                </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default ResultsPage;
